/**
 * Aplica tolerancias OT Premium en cliente usando el perfil precomputado en la OT padre.
 * Las tablas de negocio viven solo en backend (`otPremiumDimensionesTolerancias.ts`).
 */

export type OtPremiumToleranceBoundEntry = {
  value?: number;
  min?: string;
  max?: string;
};

export type OtPremiumToleranceBoundsProfile = {
  ancho?: OtPremiumToleranceBoundEntry;
  largo?: OtPremiumToleranceBoundEntry;
  espesor?: OtPremiumToleranceBoundEntry;
  fuelle?: OtPremiumToleranceBoundEntry;
  sello_fuelle?: OtPremiumToleranceBoundEntry;
  ext_ancho?: OtPremiumToleranceBoundEntry;
};

const LABEL_MATCH_ANCHO_FUELLES = ['ANCHO FUELLES', 'ANCHO FUELLE', 'FUELLE LATERAL'];
const LABEL_MATCH_ESPESOR = ['ESPESOR'];
const LABEL_MATCH_LARGO = ['LARGO'];
const LABEL_MATCH_ANCHO = ['ANCHO'];
const LABEL_EXCLUDE_ANCHO = ['ANCHOS', 'N° DE ANCHO', 'N DE ANCHO', 'FUELLE', 'FUELLES'];

function normalizeLabel(value: unknown): string {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/\s+/g, ' ')
    .trim();
}

function labelIncludesAny(label: string, needles: readonly string[]): boolean {
  return needles.some((needle) => label.includes(normalizeLabel(needle)));
}

function labelExcludesAny(label: string, needles: readonly string[]): boolean {
  return needles.some((needle) => label.includes(normalizeLabel(needle)));
}

export function getOtPremiumToleranceBoundsFromParent(parentTask: any): OtPremiumToleranceBoundsProfile | null {
  if (!parentTask) return null;
  const meta = parentTask.isTaskSourceMeta;
  if (meta?.source !== 'otPremium') return null;
  const profile = meta?.toleranceBounds;
  if (!profile || typeof profile !== 'object') return null;
  return profile as OtPremiumToleranceBoundsProfile;
}

function resolveToleranceEntryForQuestion(
  label: string,
  profile: OtPremiumToleranceBoundsProfile,
  isId24?: boolean,
): OtPremiumToleranceBoundEntry | undefined {
  // Orden específico → genérico: fuelle antes que ancho (evita que "ANCHO FUELLES" matchee ANCHO).
  if (labelIncludesAny(label, LABEL_MATCH_ANCHO_FUELLES)) {
    if (isId24) {
      return profile.sello_fuelle || profile.fuelle;
    }
    return profile.fuelle;
  }
  if (labelIncludesAny(label, LABEL_MATCH_ESPESOR)) {
    return profile.espesor;
  }
  if (labelIncludesAny(label, LABEL_MATCH_LARGO)) {
    return profile.largo;
  }
  if (labelIncludesAny(label, LABEL_MATCH_ANCHO) && !labelExcludesAny(label, LABEL_EXCLUDE_ANCHO)) {
    if (isId24) {
      return profile.ancho;
    }
    return profile.ext_ancho;
  }
  return undefined;
}

function setAlertNumberRangeFromEntry(item: any, entry: OtPremiumToleranceBoundEntry | undefined) {
  if (!entry) return;
  const min = entry.min != null && entry.min !== '' ? entry.min : null;
  const max = entry.max != null && entry.max !== '' ? entry.max : null;
  if (min == null && max == null) return;

  item.hasAlertNumberRange = true;
  item.alertNumberRange = [min, max];
  item.invertAlertNumberRange = true;
  // delete item.numberFieldMin;
  // delete item.numberFieldMax;
}

export function applyOtPremiumToleranceBoundsFromProfile(
  lista: any[],
  profile: OtPremiumToleranceBoundsProfile | null | undefined,
  isId24?: boolean,
): any[] {
  if (!profile || !Object.keys(profile).length) return lista || [];

  return (lista || []).map((item) => {
    if (item?.tipo !== 'pregunta' || item?.tipoRespuesta !== 'numberField') {
      return item;
    }

    const label = normalizeLabel(item?.pregunta?.label || item?.pregunta?.titulo || '');
    if (!label) return item;

    const entry = resolveToleranceEntryForQuestion(label, profile, isId24);
    if (!entry) return item;

    const clone = {...item};
    setAlertNumberRangeFromEntry(clone, entry);
    return clone;
  });
}

export function applyOtPremiumToleranceBoundsForChildTask(lista: any[], parentTask: any, isId24?: boolean): any[] {
  return applyOtPremiumToleranceBoundsFromProfile(
    lista,
    getOtPremiumToleranceBoundsFromParent(parentTask),
    isId24,
  );
}
